import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { superAdminApi } from "@/api/superAdmin";
import { chainApi } from "@/api/chain";

type HospitalOpt = { id: string; name: string };

/**
 * Resolve single hospitalId for accounting books (never "all").
 */
export function useAccountingHospital() {
  const { user, isChainAdmin, activeHospitalId, setActiveHospitalLocal } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isCharteredAccountant = user?.role === "CHARTERED_ACCOUNTANT";
  const canPost =
    user?.role === "SUPER_ADMIN" || user?.role === "HOSPITAL_ADMIN";
  /** Chartered accountant: all Accounting tabs, view-only (no post/edit). */
  const readOnly = isCharteredAccountant;

  const [pickedHospitalId, setPickedHospitalId] = useState("");

  const { data: superHospitals = [] } = useQuery({
    queryKey: ["super-admin-hospitals-accounting"],
    queryFn: async () => {
      const list = await superAdminApi.getHospitals();
      return (Array.isArray(list) ? list : []).filter(
        (h: HospitalOpt) => h?.id && h?.name
      ) as HospitalOpt[];
    },
    enabled: isSuperAdmin,
    staleTime: 60_000,
  });

  const { data: chainHospitals = [] } = useQuery({
    queryKey: ["chain-hospitals-accounting"],
    queryFn: async () => {
      const r = await chainApi.getGroup();
      const group = r.data?.data as { hospitals?: HospitalOpt[] } | undefined;
      return group?.hospitals ?? [];
    },
    enabled: !!isChainAdmin && !isSuperAdmin,
    staleTime: 60_000,
  });

  const hospitalOptions: HospitalOpt[] = useMemo(() => {
    if (isSuperAdmin) return superHospitals.filter((h) => h?.id && h?.name);
    if (isChainAdmin) return chainHospitals.filter((h) => h?.id && h?.name);
    if (isCharteredAccountant && (user?.hospitals?.length ?? 0) > 0) {
      return user!.hospitals!.map((h) => ({ id: h.id, name: h.name }));
    }
    if (user?.hospitals?.length) {
      return user.hospitals.map((h) => ({ id: h.id, name: h.name }));
    }
    if (user?.hospitalId) {
      return [{ id: user.hospitalId, name: "Current hospital" }];
    }
    return [];
  }, [
    isSuperAdmin,
    isChainAdmin,
    superHospitals,
    chainHospitals,
    user?.hospitals,
    user?.hospitalId,
  ]);

  const hospitalId = useMemo(() => {
    if (isSuperAdmin || isChainAdmin || isCharteredAccountant) {
      const active =
        activeHospitalId && activeHospitalId !== "all" ? activeHospitalId : "";
      if (pickedHospitalId) return pickedHospitalId;
      if (active && hospitalOptions.some((h) => h.id === active)) {
        return active;
      }
      return hospitalOptions[0]?.id ?? "";
    }
    return user?.hospitalId || activeHospitalId || "";
  }, [
    isSuperAdmin,
    isChainAdmin,
    isCharteredAccountant,
    pickedHospitalId,
    activeHospitalId,
    hospitalOptions,
    user?.hospitalId,
  ]);

  const needsHospitalPicker =
    isSuperAdmin || !!isChainAdmin || (isCharteredAccountant && hospitalOptions.length > 1);

  const setHospitalId = (id: string) => {
    setPickedHospitalId(id);
    if (isCharteredAccountant) {
      setActiveHospitalLocal(id);
    }
  };

  return {
    hospitalId,
    setHospitalId,
    hospitalOptions,
    needsHospitalPicker,
    canPost: canPost && !readOnly,
    readOnly,
    isSuperAdmin,
    isChainAdmin: !!isChainAdmin,
  };
}
